import React, { useEffect, useState } from "react";
import { ChriRecordProps, ChriRetrievalState } from "interfaces/pages/components/ChriRecordProps";
import { ChriEditor } from "../ChriEditor/ChriEditor";
import { useUserStore } from "../../../../../../state";
import { formatPlainTextForEditor } from "utils/printText";

const ChriRecord: React.FC<ChriRecordProps> = ({
  chriResult,
  transactionalInfo,
  setEditRedactedMode,
  editRedactedMode,
  setTextChanges,
  textChanges,
  chriRetrievalState,
  view,
  setView,
  saveRapsheetRedaction
}) => {
  const [originalText, setOriginalText] = useState<string>('');
  const [redactedText, setRedactedText] = useState<string>('');
  const [redactedTextCreation, setRedactionTextCreation] = useState<string>('');
  const [createdRedaction, setCreatedRedaction] = useState<boolean>(false);

  const userConfig = useUserStore(state => state.userConfig);

  const { usersPermissions } = userConfig;
  const canViewOriginalChri = usersPermissions?.["viewOriginalChri"];
  const canViewRedactedChri = usersPermissions?.["viewRedactedChri"];
  const canEditRedactedChri = usersPermissions?.["editRedactedChri"];
  const canCreateNewRedactedChri = usersPermissions?.["createNewRedactedChri"];

  useEffect(() => {
    if (chriResult) {
      if (chriResult.response_rapsheet) {
        const htmlOriginalRapsheet = formatPlainTextForEditor(chriResult.response_rapsheet);
        setOriginalText(htmlOriginalRapsheet);
      }

      if (chriResult.rapsheet_redacted) {
        setRedactedText(chriResult.rapsheet_redacted);
      }
    }
  }, [chriResult]);

  const getCardTitle = () => {
    if (chriRetrievalState === ChriRetrievalState.INITIAL) {
      return 'CHRI Record Not Yet Retrieved';
    } else if (chriRetrievalState === ChriRetrievalState.LOADING) {
      return 'Requesting Latest CHRI…';
    } else if (chriRetrievalState === ChriRetrievalState.FAIL) {
      return 'CHRI Unavailable';
    } else {
      return ''
    }
  }

  const getCardSubTitle = () => {
    if (chriRetrievalState === ChriRetrievalState.INITIAL) {
      return 'Criminal history data has not yet been retrieved for this transaction. Click “Retrieve CHRI” to request and access the official record.';
    } else if (chriRetrievalState === ChriRetrievalState.LOADING) {
      return 'Retrieval in progress — accessing the latest CHRI record on file.';
    } else if (chriRetrievalState === ChriRetrievalState.FAIL) {
      return 'No CHRI record is currently available for this transaction. You may reattempt retrieval using “Retrieve CHRI” once a record is available.';
    } else {
      return ''
    }
  }

  return (
    <>
      {chriRetrievalState !== ChriRetrievalState.SUCCESS ?
        <ChriEditor
          isPlaceholder={true}
          isEditable={false}
          textChanges={false}
          originalText={''}
          title={getCardTitle()}
          subtitle={getCardSubTitle()}
        />
        : null
      }

      {chriRetrievalState === ChriRetrievalState.SUCCESS && canViewOriginalChri && view === 'Original' ?
        <ChriEditor
          chriResult={chriResult}
          isEditable={canEditRedactedChri}
          view={view}
          setView={setView}
          textChanges={textChanges}
          setTextChanges={setTextChanges}
          viewOriginal={originalText}
          originalText={originalText}
          title="ORIGINAL"
          setEditRedactedMode={setEditRedactedMode}
          redactedTextCreation={redactedTextCreation}
          createdRedaction={createdRedaction}
          allResponsesRecieved={canCreateNewRedactedChri}
        />
        : null
      }

      {chriRetrievalState === ChriRetrievalState.SUCCESS && canViewRedactedChri && view === 'Redacted' ?
        <ChriEditor
          chriResult={chriResult}
          isEditable={canEditRedactedChri}
          view={view}
          setView={setView}
          textChanges={textChanges}
          setTextChanges={setTextChanges}
          viewOriginal={originalText}
          originalText={redactedTextCreation ? redactedTextCreation : redactedText || ''}
          title="REDACTED"
          redactedText={redactedText}
          redactedTextCreation={redactedTextCreation}
          createdRedaction={createdRedaction}
        />
        : null
      }

      {chriRetrievalState === ChriRetrievalState.SUCCESS && canViewRedactedChri && canViewOriginalChri && view === 'Original & Redacted' ?
        <ChriEditor
          chriResult={chriResult}
          isEditable={canEditRedactedChri && editRedactedMode}
          setEditRedactedMode={setEditRedactedMode}
          editRedactedMode={editRedactedMode}
          viewOriginal={originalText}
          originalText={redactedTextCreation ? redactedTextCreation : redactedText ? redactedText : originalText}
          view={view}
          setView={setView}
          setRedactionTextCreation={setRedactionTextCreation}
          setCreatedRedaction={setCreatedRedaction}
          createdRedaction={createdRedaction}
          allResponsesRecieved={true}
          setTextChanges={setTextChanges}
          textChanges={textChanges}
          redactedText={redactedText}
          saveRapsheetRedaction={saveRapsheetRedaction}
        />
        : null
      }
    </>
  );
}

export default ChriRecord;
